import { CommonModule } from '@angular/common';
import { Component, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DeploymentStatusDto, HealthStatusDto } from '@app/apiModules/DeploymentStatusDto';
import { PageHeaderComponent } from '@app/shared/page-header/page-header.component';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import moment from 'moment';
import { ReleaseMgmtService } from '@app/shared/services/releaseMgmtService';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '@app/shared/services/loader.service';
import { MatSelectModule } from '@angular/material/select';
import { FormGroup, FormsModule } from '@angular/forms';

@Component({
  selector: 'app-health-status',
  standalone: true,
  imports: [AgGridModule, MatIconModule, PageHeaderComponent, CommonModule, RouterModule,MatTooltipModule,MatSelectModule,FormsModule ],
  templateUrl: './health-status.component.html',
  styleUrls: ['./health-status.component.scss']
})
export class HealthStatusComponent
  implements OnInit {
  @ViewChild('agGrid') agGrid: AgGridAngular;
  breadcrumb: any[] = [];
  releaseId: number = 0;
  paginationPageSize = 10;
  infiniteInitialRowCount = 30;
  styleChipDiv = "width:100%; height: calc(100vh - 215px); flex: 1 1 auto; border: 1px solid #DFDFDF;";
  overlayNoRowsTemplate = `<span style="font-weight: 400; color: #757575; ">No records found</span>`;
  rowData: HealthStatusDto[] = [];
  filteredRowData: HealthStatusDto[] = [];
  pageTitle: string = 'Service Health Status';
  pageSubTitle: string = 'Assists in verifying availability and proper operation of services.';
  configEnvInfoMsg = 'Service associated with selected environment will be displayed with Health status';
  refMsg = 'Refresh to fetch the health status of services for the environment selected';
  filteredEnvironments: any;
  environmentForm: FormGroup;
  isStaticEnvironmentSelect: boolean = false;
  selectedEnv: any;
  refreshDisabled: boolean = false;
  columnDefs = [
    { headerName: 'Deployment ID', field: 'deploymentId', filter: true, flex: 1, tooltipField: 'deploymentId' },
    { headerName: 'Service Name', field: 'serviceName', filter: true, flex: 1, tooltipField: 'serviceName' },
    { headerName: 'Component', field: 'component', filter: true, flex: 1, tooltipField: 'component' },
    { headerName: 'Environment Name', field: 'environmentName', filter: true, flex: 1, tooltipField: 'environmentName' },
    {
      headerName: 'Deployment Date',
      flex: 1,
      dataType: "date",
      field: 'deploymentDate',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear'],
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          if (
            cellDate.getFullYear() === filterLocalDateAtMidnight.getFullYear() &&
            cellDate.getMonth() === filterLocalDateAtMidnight.getMonth() &&
            cellDate.getDate() === filterLocalDateAtMidnight.getDate()
          ) {
            return 0;
          }
          return -1;
        }
      },
      cellRenderer: (params: ICellRendererParams) => {
        return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
      }, tooltipValueGetter: (params: ICellRendererParams) => {
        return params.value ? moment(params.value).format('MMMM DD, YYYY at HH:mm A') : '';
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      filter: true,
      flex: 1,
      cellStyle: { padding: '0 8px' },
      cellRenderer: (params: ICellRendererParams) => {
        const btnStyle = `
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          font-weight: 500;
          cursor: default;
          white-space: nowrap;
          border: none;
          margin-left: 12px;
          box-shadow: 0 1px 2px rgba(60,60,60,0.07);
        `;
        if (params.value === 'SUCCESS') {
          return `<span style="${btnStyle} background: #168736; color: #fff;" title= "Service available and running.">
            <svg width="20" height="20" viewBox="0 0 20 20" style="display:block;">
              <polyline points="5,11 9,15 15,6" style="fill:none;stroke:white;stroke-width:3.5;stroke-linecap:round;stroke-linejoin:round"/>
            </svg>
          </span>`;
        }
        if (params.value === 'FAILED') {
          return `<span style="${btnStyle} background: #ed0000; color: #fff;" title= "Service is unavailable.">
            <svg width="20" height="20" viewBox="0 0 20 20" style="display:block;">
              <line x1="6" y1="6" x2="14" y2="14" style="stroke:white;stroke-width:3.5;stroke-linecap:round"/>
              <line x1="14" y1="6" x2="6" y2="14" style="stroke:white;stroke-width:3.5;stroke-linecap:round"/>
            </svg>
          </span>`;
        }
        if (params.value === 'URL UNAVAILABLE') {
          return `<span style="${btnStyle} background: darkgray; color: #fff;" title= "Unable to check health status for this service.">
            <svg width="20" height="20" viewBox="0 0 20 20" style="display:block;">
              <line x1="5" y1="10" x2="15" y2="10" style="stroke:white;stroke-width:3.5;stroke-linecap:round"/>
            </svg>
          </span>`;
        }

        if (params.value === 'Not Applicable') {
          return `<span style="${btnStyle} background: darkgray; color: #fff;" title= "Unable to check health status for this service.">
            <svg width="20" height="20" viewBox="0 0 20 20" style="display:block;">
              <line x1="5" y1="10" x2="15" y2="10" style="stroke:white;stroke-width:3.5;stroke-linecap:round"/>
            </svg>
          </span>`;
        }
        return `<span style="${btnStyle}">${params.value}</span>`;
      }
    },
    {
      headerName: 'Last Updated',
      flex: 1,
      dataType: "date",
      field: 'lastUpdatedDateTime',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear'],
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          if (
            cellDate.getFullYear() === filterLocalDateAtMidnight.getFullYear() &&
            cellDate.getMonth() === filterLocalDateAtMidnight.getMonth() &&
            cellDate.getDate() === filterLocalDateAtMidnight.getDate()
          ) {
            return 0;
          }
          return -1;
        }
      },
      cellRenderer: (params: ICellRendererParams) => {
        return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
      }, tooltipValueGetter: (params: ICellRendererParams) => {
        return params.value ? moment(params.value).format('MMMM DD, YYYY at HH:mm A') : '';
      },
    }
  ];

  defaultColDef = {
    minWidth: 0,
    with: 0,
    resizable: true,
    sortable: true,
    filter: true,
  };
  releaseTitle: string = '';

  constructor(private ReleaseMgmtService: ReleaseMgmtService, public route: ActivatedRoute, public dialog: MatDialog,
    private toastr: ToastrService,
    public router: Router,
    private loader: LoaderService,
    public releaseMgmtService: ReleaseMgmtService,
  ) { }
  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['releaseId']) {
        this.releaseId = params['releaseId'];
      }
    });

    this.breadcrumb = [
      { label: 'Home', command: () => this.router.navigate(['/release']) },
      {
        label: 'Release Deployment Details',
        command: () => this.router.navigate(['/release/release-deployment-dtls', this.releaseId])
      },
      { label: 'Health Status', styleClass: 'active' }
    ];

    this.releaseMgmtService.apiGetEnvironmentDetailsByRelease(this.releaseId).subscribe({
      next: (res) => {
        if (res) {
          this.filteredEnvironments = res;
          if (this.filteredEnvironments.length > 0) {
            this.selectedEnv = this.filteredEnvironments[0]; // default first
            // this.onEnvironmentChange(); // load data for default
            // Disable dropdown if only one
            this.isStaticEnvironmentSelect = this.filteredEnvironments.length === 1;
            this.filterByRelaseId();
            this.isLatestDeployment();
          }

        }
        this.loader.showSpinner.next(false);
      }, error: (error) => {
        this.loader.showSpinner.next(true);
        this.toastr.error('Failed to load environments');
      }
    })
  
  }

  onGridReady(params: any) {
    if (!params) return;
    this.agGrid.api = params.api;
    this.agGrid.columnApi = params.columnApi;
    this.filterByRelaseId();

    setTimeout(() => {
      params.api.sizeColumnsToFit();
    }, 200);
  }
  refreshTable() {
    this.revalidateHealthStatus();
  }
  revalidateHealthStatus() {
    this.loader.showSpinner.next(true);
    this.ReleaseMgmtService.apiRevalidateHealthStatus(this.releaseId).subscribe({
      next: (response: boolean) => {
        if (response) {
          this.filterByRelaseId();
        }
        else {
          this.loader.showSpinner.next(false);
          this.toastr.error("Error occurred during health status revalidation.", null);
        }
      },
      error: (error) => {
        this.loader.showSpinner.next(false);
        this.toastr.error("Error occurred during health status revalidation.");
      }
    });
  }
  filterByRelaseId() {
    this.loader.showSpinner.next(true);

    if (!this.selectedEnv || !this.selectedEnv.id) {
      return;
    }

    this.ReleaseMgmtService.apiGetHealthStatus(this.releaseId, this.selectedEnv.id).subscribe({
      next: (response: HealthStatusDto[]) => {
        this.rowData = response || [];
        this.filteredRowData = this.rowData;
        if (this.agGrid?.api) {
          this.releaseTitle = this.rowData.length > 0 ? this.rowData[0].releaseName : '';
          this.agGrid.api.setRowData(this.filteredRowData);
          this.agGrid.api.sizeColumnsToFit();
        }
        this.loader.showSpinner.next(false);
      },
      error: (error) => {
        this.toastr.error("Error occurred while getting health status data.", null, { toastClass: 'error' });
        console.error('Error fetching health status:', error);
        this.rowData = [];
        this.filteredRowData = [];
        this.loader.showSpinner.next(false);
        if (this.agGrid?.api) {
          this.agGrid.api.setRowData([]);
          this.agGrid.api.sizeColumnsToFit();
        }
      }
    });
  }

  onEnvironmentChange(){
    // Implement environment filtering logic here
    this.filterByRelaseId();
    this.isLatestDeployment();
  }

  isLatestDeployment() {
    this.releaseMgmtService.apiIsLatestDeployment(this.selectedEnv.id, this.releaseId).subscribe({
      next: (res) => {
        if (res) {
          this.refMsg = 'Refresh to fetch the health status of services for the environment selected.';
          this.refreshDisabled = false;
        }
        else
        {
          this.refreshDisabled = true;
          this.refMsg = 'Either deployment is in Progress or deployment in selected environment is not latest. Health status cannot be refreshed.';
        }
        this.loader.showSpinner.next(false);
      }, error: (error) => {
        this.loader.showSpinner.next(true);
      }
    });
  }
}
